import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Badge,
  useTheme,
  useMediaQuery,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import {
  Menu as MenuIcon,
  NotificationsNone as NotificationsIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import { Person, Logout } from '@mui/icons-material';
import AppButton from './AppButton';
import AddIcon from '@mui/icons-material/Add';
import CakeIcon from '@mui/icons-material/Cake';
import Color from './Color';

const fullDrawerWidth = 240;
const collapsedDrawerWidth = 72;

const Header = ({ currentDrawerWidth, handleMobileToggle, handleDesktopToggle, desktopOpen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    navigate('/profile');
    handleClose();
  };

  const handleSettings = () => {
    navigate('/settings');
    handleClose();
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  return (
    <AppBar
      position="fixed"
      elevation={1}
      sx={{
        backgroundColor: '#ffffff',
        border: 'none',
        borderBottom: 'none',
        boxShadow: 'rgba(0, 0, 0, 0.05) 0px 2px 15px',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        left: {
          sm: `${desktopOpen ? fullDrawerWidth : collapsedDrawerWidth}px`,
        },
        width: {
          sm: `calc(100% - ${desktopOpen ? fullDrawerWidth : collapsedDrawerWidth}px)`,
          xs: '100%',
        },
        transition: (theme) =>
          theme.transitions.create(['width', 'left'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
      }}
    >
      <Toolbar
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          minHeight: '64px',
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Left - Menu + Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={isMobile ? handleMobileToggle : handleDesktopToggle}
            color="primary"
            size="large"
            sx={{
              borderRadius: '12px',
              color: Color.primary.main,
              p: 1,
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Right - Notification + Profile + Settings */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          {isMobile ? (
            <Button
              variant="contained"
              sx={{ background: '#7C0BCC', width: '80px', px: 1.5, boxShadow: 'none' }}
              onClick={() => navigate('/cakeorder')}
              startIcon={<CakeIcon />}
            >
              Cake
            </Button>
          ) : (
            <Button
              variant="contained"
              sx={{ background: '#7C0BCC', boxShadow: 'none' }}
              onClick={() => navigate('/cakeorder')}
              startIcon={<CakeIcon />}
            >
              Cake Order
            </Button>
          )}

          {isMobile ? (
            <Button
              variant="contained"
              sx={{ background: '#D51721', minWidth: 'auto', px: 1.5, boxShadow: 'none' }}
              onClick={() => navigate('/create-order')}
              startIcon={<AddIcon />}
            >
              Order
            </Button>
          ) : (
            <Button
              variant="contained"
              sx={{ background: '#D51721', boxShadow: 'none' }}
              onClick={() => navigate('/create-order')}
              startIcon={<AddIcon />}
            >
              Create Order
            </Button>
          )}

          <Tooltip title="Notifications">
            <IconButton sx={{ p: 1, color: Color.primary.main }}>
              <Badge badgeContent={0} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {!isMobile && (
            <Tooltip title="Settings">
              <IconButton
                component={Link}
                to="/settings"
                sx={{ p: 0, color: Color.primary.main }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Profile Avatar & Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, pr: { xs: 0.7, md: 2 } }}>
            <Tooltip title="Profile">
              <Avatar
                alt="User"
                src="/profile.png"
                sx={{
                  width: 36,
                  height: 36,
                  cursor: 'pointer',
                  background: Color.primary.main,
                }}
                onClick={handleAvatarClick}
              >
                <Person />
              </Avatar>
            </Tooltip>

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              onClick={handleClose}
              PaperProps={{
                elevation: 3,
                sx: {
                  mt: 1.5,
                  minWidth: 150,
                },
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleProfile}>
                <ListItemIcon>
                  <Person fontSize="small" />
                </ListItemIcon>
                Profile
              </MenuItem>
              <MenuItem onClick={handleSettings}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                Settings
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;